import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";

/**
 * Sticky red bar that appears whenever the current session was minted
 * by /auth/impersonate. The platform_admin gets one big visible
 * reminder + a single click to exit the borrowed session.
 *
 * Mounted at the top of FrontendLayout so it sits above every dashboard
 * shell (club admin, coach, athlete) regardless of which one the
 * impersonated role lands in.
 */
export default function ImpersonationBanner() {
  const { impersonator, user, stopImpersonation } = useAuth();
  const [stopping, setStopping] = useState(false);

  if (!impersonator) return null;

  const targetLabel = user?.full_name || user?.email || "this user";

  const handleStop = async () => {
    setStopping(true);
    try {
      await stopImpersonation();
    } catch (err) {
      console.error("Failed to stop impersonation:", err);
      setStopping(false);
    }
  };

  return (
    /*
      No sticky, no z-index. This was `sticky top-0 z-[70]`, which put it above
      every dialog in the app — the bottom of a modal vanished behind it — and
      made it overlap the sticky header it shared top:0 with. It belongs in
      normal flow: seen on load, out of the way afterwards.
    */
    <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md">
      <div className="px-4 py-2.5 mx-auto flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <span>
            Conectat ca <strong>{targetLabel}</strong>. Sesiune de debug deschisă de{" "}
            <strong>{impersonator.full_name || impersonator.email}</strong>.
          </span>
        </div>
        <button
          onClick={handleStop}
          disabled={stopping}
          className="px-3 py-1.5 bg-white/15 hover:bg-white/25 disabled:opacity-50 text-white text-xs font-semibold rounded-lg border border-white/30 transition-colors flex items-center gap-1.5"
        >
          {stopping ? (
            <>
              <div className="w-3 h-3 border-2 border-white/60 border-t-white rounded-full animate-spin"></div>
              Se ieșea...
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Înapoi la contul tău
            </>
          )}
        </button>
      </div>
    </div>
  );
}
